import { useState } from "react";
import { TextInput } from "./Field";
import { DEFAULT_CAPACITY_DAYS_PER_WEEK, isCapacity } from "../../../src/shared/capacity";

/** What the form hands back. Capacity is null for "the team default". */
export interface MemberFormValue {
  name: string;
  role: string;
  email: string;
  capacityDaysPerWeek: number | null;
}

/**
 * The inline row for adding or editing a team member on Settings › Team.
 *
 * Capacity is the one field with a meaning for "blank": it is the team
 * default, so the placeholder shows that number rather than nothing and the
 * save sends null. A value off the half-day grid, or outside 0–7, disables
 * Save instead of being rounded — the server refuses it too.
 */
export function MemberForm({
  initial,
  onSave,
  onCancel,
  onLeave,
}: {
  initial: MemberFormValue;
  onSave: (v: MemberFormValue) => Promise<void>;
  onCancel: () => void;
  /** Marks the member as having left the team. The server refuses while
   *  work still points at them, and its reason is shown under the row. */
  onLeave?: () => Promise<void>;
}) {
  const [name, setName] = useState(initial.name);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState(initial.role);
  const [email, setEmail] = useState(initial.email);
  const [capacity, setCapacity] = useState(initial.capacityDaysPerWeek === null ? "" : String(initial.capacityDaysPerWeek));
  const [busy, setBusy] = useState(false);
  const capacityInvalid = capacity.trim() !== "" && !isCapacity(Number(capacity));
  const invalid = name.trim() === "" || role.trim() === "" || capacityInvalid;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_130px_90px_auto] items-center gap-3 border-b border-[var(--border)] px-4 py-3">
      <TextInput aria-label="Member name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
      <TextInput aria-label="Member email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email (for My work)" />
      <TextInput aria-label="Member role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role" />
      <TextInput
        aria-label="Member capacity"
        type="number"
        inputMode="decimal"
        min={0.5}
        max={7}
        step={0.5}
        value={capacity}
        onChange={(e) => setCapacity(e.target.value)}
        placeholder={`${DEFAULT_CAPACITY_DAYS_PER_WEEK} d/wk`}
        title="Focus-days a week for request work; blank means the team default"
      />
      <div className="flex justify-end gap-2">
        {onLeave && (
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onLeave();
              } catch (e) {
                setError(e instanceof Response ? await e.text() : e instanceof Error ? e.message : "Could not update member");
              } finally {
                setBusy(false);
              }
            }}
            className="rounded-md px-2 py-1 text-xs font-medium text-error-600 hover:text-error-700 focus-visible:focus-ring disabled:opacity-50"
          >
            Left the team
          </button>
        )}
        <button type="button" onClick={onCancel} className="rounded-md px-2 py-1 text-xs font-medium text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring">
          Cancel
        </button>
        <button
          type="button"
          disabled={busy || invalid}
          onClick={async () => {
            setBusy(true);
            try {
              await onSave({ name: name.trim(), role: role.trim(), email: email.trim(), capacityDaysPerWeek: capacity.trim() === "" ? null : Number(capacity) });
            } finally {
              setBusy(false);
            }
          }}
          className="rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
        >
          Save
        </button>
      </div>
      {error && (
        <p role="alert" className="col-span-full text-xs text-error-600">
          {error}
        </p>
      )}
    </div>
  );
}
